import { useRef } from "react";
import "../styles/Call.scss";
import LoomEmbed from "../components/LoomEmbed";
import CalendlyEmbed from "../components/CalendlyEmbed";

const LOOM_URL = "https://www.loom.com/embed/9675d0489de14d259bb38b18e673b665";
const CALENDLY_URL = "https://calendly.com/skilldingkevin/after-the-audit";

const FAQ_ITEMS = [
    {
        q: "Isn't this just a Patreon?",
        a: "No. Patreon is a payment wall — listeners pay, you post extra. A Club runs the other direction: you ask, they answer, and you find out what your audience actually wants. Money is a possible outcome, not the mechanism.",
    },
    {
        q: "Isn't it just a Discord?",
        a: "A Discord is open-ended — a room with no natural end point, which is a big part of why most go quiet within a month. A Club runs for a fixed stretch of time, tied to your episodes, so the focus stays concentrated instead of thinning out over months.",
    },
    {
        q: "My audience isn't big enough.",
        a: "Audience size doesn't matter here. A Club works with thirty listeners or thirty thousand — what matters is whether they're actually there to learn something, not just half-listening in the background.",
    },
    {
        q: "How much time does it take?",
        a: "Under an hour a week once it's running. If it needed more you wouldn't keep it up, and a Club nobody maintains produces nothing.",
    },
    {
        q: "Do I have to charge for it?",
        a: "No. Some podcasters run one free and use what they learn to shape the show and sell what they already sell. Others charge for access and it becomes revenue on its own. Both work — we'd talk through which fits your show.",
    },
    {
        q: "I'm ad-supported, does this apply?",
        a: "Yes, in two ways. Audience data you can actually stand behind is worth something to sponsors. And the Club itself can be a paid product independent of the ads.",
    },
    {
        q: "Who are you?",
        a: [
            "I'm Kevin, founder of Podsy. Our show got past 30,000 downloads and produced crickets and tumbleweed — no leads, no revenue, barely a reply.",
            "It took us a while to work out what was actually broken. Then I had 350+ calls with podcasters and realised the same things were wrong on nearly every show. The Club is what I built to fix that.",
        ],
    },
];

const Call = () => {
    const calendlyRef = useRef(null);

    const scrollToCalendly = (evt) => {
        evt.preventDefault();
        calendlyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="lead-page">
            <main className="lead-main">
                <header className="lead-header">
                    <h1 className="lead-h1">Your episodes should be bringing you business. Here's how it can...</h1>
                </header>

                <section className="lead-section">
                    <h2 className="lead-h2"><span className="lead-h2__step">Step 1 of 2:</span> Watch video</h2>
                    <LoomEmbed url={LOOM_URL} title="Podsy walkthrough video" />
                </section>

                <section className="lead-section lead-body--center">
                    <div className="lead-body" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <p>A Podcast Club turns your listeners into people you can actually talk to.</p>
                        <ul className="lead-list lead-list--narrow">
                            <li>Not a Patreon</li>
                            <li>Not a Discord</li>
                            <li>Not more downloads</li>
                            <li>Not ads</li>
                        </ul>
                        <p>We don't help you grow your audience. We help you engage the one you already have.</p>
                    </div>
                </section>

                <hr className="lead-hr" />

                <section className="lead-section">
                    <h2 className="lead-h2"><span className="lead-h2__step">Step 2 of 2:</span> Book a call</h2>

                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "2.5rem" }}>
                        <a href="#calendly-embed" className="lead-cta" onClick={scrollToCalendly}>
                            Book a Call
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="9 6 15 12 9 18" />
                            </svg>
                        </a>
                    </div>

                    <div className="lead-body--center" style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
                        <div>
                            <h3 style={{ marginBottom: "0.5rem", fontSize: "1rem", fontWeight: 600, color: "#fff" }}>Only book if</h3>
                            <p className="lead-body">
                                You have at least 20 episodes and you have a good reason why it would be
                                useful to know who your listeners are.
                            </p>
                        </div>
                        <div>
                            <h3 style={{ marginBottom: "0.5rem", fontSize: "1rem", fontWeight: 600, color: "#fff" }}>Don't book if</h3>
                            <p className="lead-body">
                                You launched last month, you're looking for help growing downloads, or you're
                                happy with the show exactly as it is. All three are fine answers — this just
                                isn't the thing for them.
                            </p>
                        </div>
                        <div>
                            <h3 style={{ marginBottom: "0.5rem", fontSize: "1rem", fontWeight: 600, color: "#fff" }}>What happens on the call</h3>
                            <p className="lead-body" style={{ marginBottom: "1rem" }}>
                                We go through what your show is already telling you, whether a Club would
                                produce anything useful for your audience, and what running one actually takes
                                each week. If it's a bad fit I'll say so on the call — that's a faster answer
                                for both of us.
                            </p>
                            <p className="lead-body">You'll speak to me, not a sales team.</p>
                        </div>
                    </div>

                    <div id="calendly-embed" ref={calendlyRef}>
                        <CalendlyEmbed url={CALENDLY_URL} />
                    </div>
                </section>

                <hr className="lead-hr" />

                <section>
                    <h2 className="lead-h2">FAQ</h2>
                    <div className="lead-faq">
                        {FAQ_ITEMS.map((item) => (
                            <details key={item.q}>
                                <summary>
                                    {item.q}
                                    <span className="lead-faq__chevron">+</span>
                                </summary>
                                <div className="lead-faq__answer">
                                    {Array.isArray(item.a)
                                        ? item.a.map((p) => <p key={p}>{p}</p>)
                                        : <p>{item.a}</p>}
                                </div>
                            </details>
                        ))}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Call;
